import React, {useState, useEffect} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSort} from "@fortawesome/free-solid-svg-icons";

import "./Table.css";
import {Pagination} from "./pagination/Pagination";
import {formatDateAsString, removeCamelCase} from "../../../util/StringUtil";
import {StandardButton} from "../button/standard-button/StandardButton";
import {faCheckSquare, faSquare} from "@fortawesome/free-regular-svg-icons";
import {TableSearch} from "./table-search/TableSearch";
import {TableFilter} from "./table-filter/TableFilter";

export const SelectableTable = ({
                                    tableData,
                                    onRowClick,
                                    buttonText,
                                    onButtonClick,
                                    buttonDisabled,
                                    buttonTwoText,
                                    onButtonTwoClick,
                                    buttonTwoDisabled,
                                    hiddenColumns,
                                    noResultsMessage,
                                    searchEnabled
                                }) => {
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [tableDataPages, setTableDataPages] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [ascendingSort, setAscendingSort] = useState(true);
    const [filteredColumns, setFilteredColumns] = useState([]);

    useEffect(() => {
        setFilteredTableData([...tableData]);
    }, []);

    useEffect(() => {
        let copyOfFilteredTableData = [...filteredTableData];
        const pages = [];
        while (copyOfFilteredTableData.length) {
            pages.push(copyOfFilteredTableData.splice(0, 8));
        }
        setTableDataPages([...pages]);
    }, [filteredTableData]);

    const sort = (key) => {
        let copyOfFilteredTableData = [...filteredTableData];
        copyOfFilteredTableData.sort((objectOne, objectTwo) => {
            let valueOne = objectOne[key];
            let valueTwo = objectTwo[key];
            if (typeof valueOne === "string") {
                valueOne = valueOne.toLowerCase();
                valueTwo = valueTwo.toLowerCase();
            }
            if (valueOne < valueTwo) {
                return -1;
            }
            if (valueOne > valueTwo) {
                return 1;
            }
            return 0;
        });

        if (ascendingSort) {
            setAscendingSort(false);
        } else {
            copyOfFilteredTableData = copyOfFilteredTableData.reverse();
            setAscendingSort(true);
        }
        setFilteredTableData([...copyOfFilteredTableData]);
        setCurrentPage(0);
    }

    const handleRowClick = (object) => {
        if (onRowClick) {
            onRowClick(object);
        }
    }

    const buildTableHeader = () => {
        let headers;
        if (filteredTableData.length !== 0) {
            const objectKeys = Object.keys(filteredTableData[0]);
            const numberOfColumns = objectKeys.length - hiddenColumns.length - filteredColumns.length;
            headers = objectKeys.map((key, index) => {
                if (!hiddenColumns.includes(key) && !filteredColumns.includes(key)) {
                    return (
                        <th key={index} style={{width: `calc(100vw/${numberOfColumns})`}}
                            onClick={() => {
                                sort(key)
                            }}>
                            <div className="table-header-content">
                                <div className="table-header-text">
                                    {removeCamelCase(key)}
                                </div>
                                <div className="table-header-icon">
                                    <FontAwesomeIcon icon={faSort}/>
                                </div>
                            </div>
                        </th>);
                }
            });
        }

        return (
            <thead className="table-header">
            <tr className="table-header-row">
                {headers}
            </tr>
            </thead>
        );
    }

    const buildTableBody = () => {
        const rows = tableDataPages[currentPage].map((object, index) => {
            const objectValues = [];
            for (const property in object) {
                if (!hiddenColumns.includes(property) && !filteredColumns.includes(property)) {
                    objectValues.push(object[property]);
                }
            }

            let tableDataElements = [];
            if (filteredTableData.length > 0) {
                const objectKeys = Object.keys(filteredTableData[0]);
                const numberOfColumns = objectKeys.length - hiddenColumns.length - filteredColumns.length;
                tableDataElements = objectValues.map((value, index) => {
                    let tableData;
                    if (value === null) {
                        tableData = "";
                    } else {
                        tableData = value.toString();
                    }

                    if (tableData === "") {
                        return <td style={{maxWidth: `calc(80vw/${numberOfColumns})`}} key={index}>N/A</td>
                    } else if (tableData === "true") {
                        return <td key={index}><FontAwesomeIcon className="capture-modal-list-item-icon"
                                                                icon={faCheckSquare}/></td>
                    } else if (tableData === "false") {
                        return <td key={index}><FontAwesomeIcon className="capture-modal-list-item-icon"
                                                                icon={faSquare}/></td>
                    } else if (tableData.includes("20" && "-" && "Z")) {
                        return <td style={{maxWidth: `calc(80vw/${numberOfColumns})`}}
                                   key={index}>{formatDateAsString(new Date(tableData))}</td>
                    } else {
                        return <td style={{maxWidth: `calc(80vw/${numberOfColumns})`}} key={index}>{tableData}</td>
                    }
                });
            }

            return (
                <tr key={index} className="table-body-row" onClick={() => {
                    handleRowClick(object)
                }}>
                    {tableDataElements}
                </tr>
            );
        });

        return (
            <tbody className="table-body">
            {rows}
            </tbody>
        );
    }

    const tableHeader = buildTableHeader();
    let tableBody;
    if (tableDataPages[currentPage]) {
        tableBody = buildTableBody();
    }

    return (
        <div className="table-container">
            <div className="table-control-container">
                <div className="table-search-filter-container">
                    {searchEnabled && tableData.length > 0 &&
                    <TableSearch dataToFilter={tableData}
                                 setFilteredData={setFilteredTableData}
                                 setCurrentPage={setCurrentPage}/>}
                    <TableFilter dataToFilter={tableData}
                                 setFilteredData={setFilteredTableData}
                                 hiddenColumns={hiddenColumns}
                                 setCurrentPage={setCurrentPage}
                                 filteredColumns={filteredColumns}
                                 setFilteredColumns={setFilteredColumns}/>
                </div>
                <div className="table-button-container">
                    {buttonText &&
                    <StandardButton text={buttonText} onClick={onButtonClick} disabled={buttonDisabled}/>}
                    {buttonTwoText &&
                    <StandardButton text={buttonTwoText} onClick={onButtonTwoClick} disabled={buttonTwoDisabled}/>}
                </div>
            </div>
            <table className="table">
                {tableHeader}
                {tableBody}
            </table>
            {filteredTableData.length === 0 && <div
                className="table-no-results-container">{noResultsMessage ? noResultsMessage : "No Results Found"}</div>}
            {filteredTableData.length > 0 &&
            <Pagination pageCount={tableDataPages.length} currentPage={currentPage}
                        setCurrentPage={setCurrentPage}/>}
        </div>
    );
}
